import { Translate } from "@omni/types/utils/translate";
import { isDefined } from "@omni/utils/helper.utils";
import { useCallback, useEffect, useMemo } from "react";
import { DefaultValues } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";

import { useCustomerServiceApi } from "@/api/hooks/customerService.hook";
import { getPhone } from "@/components/ContactCustomerservice/helpers/formValues.helpers";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import * as CustomerSelectors from "@/redux/modules/api/data/customer/customer.selectors";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import {
    ContactQuizziFormValues,
    QuizziAppointmentComponent
} from "./quizziAppointment.component";
import {
    CONTACT_QUIZZI_FAILLURE_MESSAGE_ID,
    CONTACT_QUIZZI_SUCCESS_MESSAGE_ID
} from "./quizziAppointment.config";
import { ContactQuizziRequest } from "./types/contactQuizziRequest.types";

const broadcastSuccessMessage = (t: Translate) => () =>
    broadcastMessage({
        id: CONTACT_QUIZZI_SUCCESS_MESSAGE_ID,
        text: t("contact.quizziStore.request.success.message"),
        status: "success",
        dismissible: true,
        activeScroll: true
    });

const broadcastFailureMessage = (t: Translate) => () =>
    broadcastMessage({
        id: CONTACT_QUIZZI_FAILLURE_MESSAGE_ID,
        text: t("contact.quizziStore.request.failure.message"),
        status: "danger",
        dismissible: true,
        activeScroll: true
    });

export const QuizziAppointment = () => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();

    const isLoadingCustomer = useSelector(CustomerSelectors.isLoading);
    const isLoadingPhonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.isLoading
    );

    const customer = useSelector(CustomerSelectors.getCustomer, shallowEqual);
    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );

    const [{ loading: contactQuizziLoading }, contactQuizziStore] =
        useCustomerServiceApi<ContactQuizziRequest>(
            {
                url: "/quizziform",
                method: "POST"
            },
            { manual: true, useCache: false }
        );

    useEffect(() => {
        dispatch(CustomerModule.load());
    }, [dispatch]);

    const loading =
        contactQuizziLoading ||
        isLoadingCustomer ||
        isLoadingPhonenumberPrefixes;

    const initialValues: DefaultValues<ContactQuizziFormValues> = useMemo(
        () => ({
            lastName: customer?.lastName ?? "",
            firstName: customer?.firstName,
            email: customer?.email ?? "",
            storeId: "",
            phone: customer?.phone ?? "",
            message: "",
            preferredDaysOfWeek: [],
            phonePrefix: customer?.phonePrefix || "+32"
        }),
        [customer]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const handleOnSubmit = useCallback(
        async (values: ContactQuizziFormValues) => {
            clearMessageById(CONTACT_QUIZZI_SUCCESS_MESSAGE_ID);
            clearMessageById(CONTACT_QUIZZI_FAILLURE_MESSAGE_ID);

            const request: ContactQuizziRequest = {
                firstName: values.firstName || "",
                lastName: values.lastName,
                message: values.message,
                storeId: values.storeId,
                email: values.email,
                preferredDaysOfWeek: (values.preferredDaysOfWeek || []).filter(
                    isDefined
                ),
                phone: getPhone(
                    phonenumberPrefixes,
                    values.phonePrefix,
                    values.phone
                )
            };

            return contactQuizziStore({ data: request })
                .then(broadcastSuccessMessage(t))
                .catch(broadcastFailureMessage(t));
        },
        [phonenumberPrefixes, initialValues, t, contactQuizziStore]
    );

    return (
        <QuizziAppointmentComponent
            loading={loading}
            submitting={false}
            initialValues={initialValues}
            onSubmit={handleOnSubmit}
        />
    );
};
